import { Shield, Target, Users, Zap, Trophy } from "lucide-react";
import { type MultipliersResponse, type CategoryData } from "@/api/multipliers";

const formatStatName = (statName: string): string => {
    return statName
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase())
        .replace(/Pct/g, "%")
        .replace(/Ppm/g, "per minute")
        .replace(/Xg/g, "Expected Goals")
        .replace(/Corsi/g, "CORSI")
        .replace(/Fenwick/g, "FENWICK")
        .replace(/Oish/g, "Full Strength Shooting")
        .replace(/Oisv/g, "Full Strength Save")
        .replace(/TM/g, "");
};

const transformBackendDataToComponents = (data: MultipliersResponse) => {
    const categoryMap = {
        offensive: { title: "Offensive", icon: <Target className="w-5 h-5" />, bgColor: "var(--color-general-red)" },
        defensive: { title: "Defensive", icon: <Shield className="w-5 h-5" />, bgColor: "var(--color-general-green)" },
        teamplay: { title: "Team Play", icon: <Users className="w-5 h-5" />, bgColor: "var(--color-general-yellow)" },
        effort: { title: "Effort", icon: <Zap className="w-5 h-5" />, bgColor: "var(--color-general-purple)" },
        bonus: { title: "Bonus", icon: <Trophy className="w-5 h-5" />, bgColor: "var(--color-general-orange)" },
    };

    return Object.entries(categoryMap).map(([key, config]) => {
        const categoryData = data[key as keyof typeof data] as CategoryData[] | undefined;

        if (!categoryData || !Array.isArray(categoryData)) {
            return {
                ...config,
                maxPoints: 0 as number | null,
                hasUncapped: false,
                metrics: [] as Array<{ name: string; maxPoints: number | null; description: string }>,
            };
        }

        let totalMaxPoints: number | null = 0;
        let hasUncapped = false;
        const metrics: Array<{
            name: string;
            maxPoints: number | null;
            description: string;
        }> = [];

        categoryData.forEach((group) => {
            if (group.max && group.max.isActive) {
                const maxPoints = group.max.maxPoints;

                if (maxPoints !== null) {
                    if (totalMaxPoints !== null) {
                        totalMaxPoints += maxPoints;
                    }
                } else {
                    totalMaxPoints = null;
                    hasUncapped = true;
                }

                if (key === "bonus") {
                    return;
                }

                metrics.push({
                    name: formatStatName(group.max.statName),
                    maxPoints: maxPoints,
                    description: group.max.description ?? "",
                });
            }
        });

        if (key === "bonus") {
            const goalsWeight = data.bonus?.[0]?.weights?.find((w) => w.statName === "goals");
            const assistsWeight = data.bonus?.[0]?.weights?.find((w) => w.statName === "assists");

            metrics.push({
                name: "Goals",
                maxPoints: goalsWeight?.weight ?? 3,
                description: goalsWeight?.description ?? "",
            });

            metrics.push({
                name: "Assists",
                maxPoints: assistsWeight?.weight ?? 2.5,
                description: assistsWeight?.description ?? "",
            });
        }

        return {
            ...config,
            maxPoints: totalMaxPoints,
            hasUncapped,
            metrics,
        };
    });
};

interface KpisComponentsProps {
    data: MultipliersResponse;
    isDefault?: boolean;
}

export const KpisComponents = ({ data }: KpisComponentsProps) => {
    const components = transformBackendDataToComponents(data);
    return (
        <div className="space-y-4">
            <h3 className="text-base font-headline font-bold text-on-surface">Component Breakdown</h3>
            <div className="grid grid-cols-1 gap-3 items-stretch sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 sm:gap-4 xl:gap-6">
                {components.map((component, index) => {
                    const cappedTotal = component.metrics
                        .filter((m) => m.maxPoints !== null)
                        .reduce((sum, m) => sum + (m.maxPoints ?? 0), 0);

                    return (
                        <div
                            key={component.title}
                            className="glass card card-slide-up rounded-xl p-3 flex flex-col h-full sm:p-4 xl:p-5"
                            style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                        >
                            <div className="flex items-center gap-2.5 mb-2.5 sm:hidden">
                                <div
                                    className="p-1.5 rounded-full text-white shrink-0"
                                    style={{ backgroundColor: component.bgColor }}
                                >
                                    {component.icon}
                                </div>
                                <div>
                                    <h3 className="text-[13px] font-semibold text-on-surface leading-tight">
                                        {component.title}
                                    </h3>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-base font-bold text-on-surface">
                                            {component.maxPoints !== null ? component.maxPoints : "∞"}
                                        </span>
                                        <span className="text-[10px] text-on-surface-variant">
                                            {component.maxPoints !== null ? "max pts" : "uncapped"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="hidden sm:flex flex-col items-center text-center mb-4 xl:mb-5">
                                <div
                                    className="p-2 rounded-full text-white mb-2 xl:p-3"
                                    style={{ backgroundColor: component.bgColor }}
                                >
                                    {component.icon}
                                </div>
                                <h3 className="text-sm font-semibold text-on-surface">{component.title}</h3>
                                <div className="text-xl font-bold text-on-surface xl:text-2xl">
                                    {component.maxPoints !== null ? component.maxPoints : "∞"}
                                </div>
                                <div className="text-xs text-on-surface-variant">
                                    {component.maxPoints !== null ? "max points" : "uncapped"}
                                </div>
                            </div>
                            <div className="space-y-1.5 sm:space-y-2.5 flex-1">
                                {component.metrics
                                    .filter((metric) => {
                                        if (component.title === "Bonus") {
                                            return metric.name === "Goals" || metric.name === "Assists";
                                        }
                                        return true;
                                    })
                                    .map((metric) => {
                                        const isUncapped = metric.maxPoints === null;
                                        const percentage =
                                            !isUncapped && cappedTotal > 0
                                                ? ((metric.maxPoints ?? 0) / cappedTotal) * 100
                                                : 0;

                                        return (
                                            <div key={metric.name} className="space-y-1">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xs text-on-surface">{metric.name}</p>
                                                    <span className="text-xs font-bold tabular-nums text-on-surface">
                                                        {isUncapped ? "∞" : metric.maxPoints}
                                                    </span>
                                                </div>
                                                <div className="relative w-full h-1 bg-surface-container rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={
                                                            isUncapped
                                                                ? {
                                                                      width: "100%",
                                                                      background: `linear-gradient(90deg, ${component.bgColor} 0%, transparent 100%)`,
                                                                  }
                                                                : {
                                                                      width: `${Math.min(percentage, 100)}%`,
                                                                      backgroundColor: component.bgColor,
                                                                  }
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
